"use client";

import { useState, useEffect, type ComponentProps } from "react";
import Button from "../ui/Button";
import styles from "./Header.module.css";

// Assets constants based on Figma export
const IMG_LOGO = "/images/logo-flex.png";
const IMG_CHEVRON = "/images/placeholders/chevron down.svg";

import NextLink from "next/link";
import Image from "next/image";

function Link(props: ComponentProps<typeof NextLink>) {
    return <NextLink {...props} prefetch={false} />;
}

export default function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);

    // Lock body scroll when menu is open
    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;

        if (isMenuOpen) {
            html.style.overflow = "hidden";
            body.style.overflow = "hidden";
            html.style.height = "100%";
            body.style.height = "100%";
        } else {
            html.style.overflow = "";
            body.style.overflow = "";
            html.style.height = "";
            body.style.height = "";
        }
        return () => {
            html.style.overflow = "";
            body.style.overflow = "";
            html.style.height = "";
            body.style.height = "";
        };
    }, [isMenuOpen]);

    const toggleAccordion = (item: string) => {
        if (window.innerWidth <= 768) {
            setExpandedItem(expandedItem === item ? null : item);
        }
    };

    return (
        <header className={styles.header}>
            <div className={styles.container}>
                <div className={styles.logoContainer}>
                    <Link href="/">
                        <Image src={IMG_LOGO} alt="Flexpro Logo" width={140} height={36} className={styles.logo} priority quality={60} />
                    </Link>
                </div>

                <button
                    className={styles.hamburger}
                    onClick={() => {
                        setIsMenuOpen(!isMenuOpen);
                        if (isMenuOpen) setExpandedItem(null);
                    }}
                    aria-label="Toggle menu"
                >
                    <div className={`${styles.bar} ${isMenuOpen ? styles.barOpen1 : ''}`}></div>
                    <div className={`${styles.bar} ${isMenuOpen ? styles.barOpen2 : ''}`}></div>
                    <div className={`${styles.bar} ${isMenuOpen ? styles.barOpen3 : ''}`}></div>
                </button>

                <div className={`${styles.navContainer} ${isMenuOpen ? styles.navOpen : ''}`}>
                    <nav className={styles.nav}>
                        {/* Soluções */}
                        <div
                            className={`${styles.navItem} ${expandedItem === "solucoes" ? styles.navItemActive : ''}`}
                            onClick={() => toggleAccordion("solucoes")}
                        >
                            <div className={styles.navItemContent}>
                                <span className={styles.navText}>Soluções</span>
                                <Image src={IMG_CHEVRON} alt="Expand" width={16} height={16} className={styles.chevron} />
                            </div>

                            <div className={styles.megaMenuWrapper}>
                                <div className={`${styles.megaMenuContainer} ${styles.solutionsMegaMenu}`}>
                                    {/* Left Promo Box */}
                                    <div className={styles.promoBox}>
                                        <h3>Um ecossistema para a operação imobiliária</h3>
                                        <p>Da gestão da locação ao relacionamento com clientes, as soluções da Flexpro se conectam para organizar processos, automatizar o previsível e dar visibilidade real da operação.</p>
                                    </div>

                                    <div className={styles.ecosystemDropdown}>
                                        <div className={styles.dropHeader}>
                                            <span className={styles.dropHeaderTitle}>Ecossistema Flexpro</span>
                                        </div>
                                        <Link className={`${styles.dropItem} ${styles.iconDropItem}`} href="/sistema-para-imobiliaria">
                                            <div className={styles.dropItemIcon} aria-hidden="true">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                                    <path d="M3.75 3A1.75 1.75 0 0 0 2 4.75v14.5c0 .966.784 1.75 1.75 1.75h7v-1.5H3.75a.25.25 0 0 1-.25-.25V4.75a.25.25 0 0 1 .25-.25h16.5a.25.25 0 0 1 .25.25V10H22V4.75A1.75 1.75 0 0 0 20.25 3H3.75Z" />
                                                    <path d="M6 7.75A.75.75 0 0 1 6.75 7h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 6 7.75ZM6.75 10a.75.75 0 0 0 0 1.5h.5a.75.75 0 0 0 0-1.5h-.5ZM6 13.75A.75.75 0 0 1 6.75 13h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 6 13.75ZM10.75 7a.75.75 0 0 0 0 1.5h.5a.75.75 0 0 0 0-1.5h-.5ZM10 10.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1-.75-.75ZM15.75 12a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5ZM10.5 15.75a5.25 5.25 0 1 1 10.5 0 5.25 5.25 0 0 1-10.5 0Z" />
                                                    <path d="M15 14.25a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 0 1.5H15.75A.75.75 0 0 1 15 14.25ZM15.75 16a.75.75 0 0 0-.75.75v1a.75.75 0 0 0 1.5 0v-1A.75.75 0 0 0 15.75 16Z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className={styles.dropItemLabel}>SIMOB</div>
                                                <div className={styles.dropItemDesc}>ERP completo para gestão imobiliária, locação, financeiro, contratos e BI</div>
                                            </div>
                                        </Link>
                                        <Link className={`${styles.dropItem} ${styles.iconDropItem}`} href="/crm-para-imobiliarias">
                                            <div className={styles.dropItemIcon} aria-hidden="true">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm0 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM15.375 12a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM12 5.25a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0V6a.75.75 0 0 1 .75-.75Zm0 12a.75.75 0 0 1 .75.75v.75a.75.75 0 0 1-1.5 0V18a.75.75 0 0 1 .75-.75ZM5.25 12a.75.75 0 0 1 .75-.75h.75a.75.75 0 0 1 0 1.5H6a.75.75 0 0 1-.75-.75Zm12 0a.75.75 0 0 1 .75-.75h.75a.75.75 0 0 1 0 1.5H18a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className={styles.dropItemLabel}>MOOV</div>
                                                <div className={styles.dropItemDesc}>Site + CRM imobiliário para converter leads em negócios</div>
                                            </div>
                                        </Link>
                                        <Link className={`${styles.dropItem} ${styles.iconDropItem}`} href="/aplicativos-para-imobiliarias/relacionamento-com-inquilinos">
                                            <div className={styles.dropItemIcon} aria-hidden="true">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                                    <path d="M10.5 18.75a.75.75 0 0 0 0 1.5h3a.75.75 0 0 0 0-1.5h-3Z" />
                                                    <path fillRule="evenodd" d="M8.625 3A2.625 2.625 0 0 0 6 5.625v12.75A2.625 2.625 0 0 0 8.625 21h6.75A2.625 2.625 0 0 0 18 18.375V5.625A2.625 2.625 0 0 0 15.375 3h-6.75ZM7.5 5.625A1.125 1.125 0 0 1 8.625 4.5h6.75A1.125 1.125 0 0 1 16.5 5.625v12.75a1.125 1.125 0 0 1-1.125 1.125h-6.75A1.125 1.125 0 0 1 7.5 18.375V5.625Z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className={styles.dropItemLabel}>Sitalk</div>
                                                <div className={styles.dropItemDesc}>App com a sua marca para inquilinos e proprietários</div>
                                            </div>
                                        </Link>
                                        <Link className={`${styles.dropItem} ${styles.iconDropItem}`} href="/aplicativos-para-imobiliarias/gestao-operacional-imobiliaria">
                                            <div className={styles.dropItemIcon} aria-hidden="true">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                                    <path fillRule="evenodd" d="M12 6.75a5.25 5.25 0 0 1 6.775-5.025.75.75 0 0 1 .313 1.248l-3.32 3.319c.063.475.276.934.641 1.299.365.365.824.578 1.3.641l3.318-3.319a.75.75 0 0 1 1.248.313 5.25 5.25 0 0 1-5.472 6.756c-1.018-.086-1.87.1-2.309.634L7.344 21.3A3.298 3.298 0 1 1 2.7 16.657l8.684-7.151c.533-.44.72-1.291.634-2.309A5.342 5.342 0 0 1 12 6.75ZM4.117 19.125a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75h-.008a.75.75 0 0 1-.75-.75v-.008Z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div className={styles.dropItemLabel}>Tools</div>
                                                <div className={styles.dropItemDesc}>App de vistorias e operações em campo para sua equipe</div>
                                            </div>
                                        </Link>
                                        <div className={styles.legacySolutionsLinks}>
                                        {/* SIMOB Column */}
                                        <div className={styles.linkColumn}>
                                            <h4><Link href="/sistema-para-imobiliaria">SIMOB</Link></h4>
                                            <ul>
                                                <li><Link href="/sistema-para-imobiliaria/locacao">Locação</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/vendas">Vendas</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/financeiro">Financeiro</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/condominio">Condomínio</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/juridico">Jurídico</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/caixa">Caixa</Link></li>
                                                <li><Link href="/sistema-para-imobiliaria/bi">Business Intelligence</Link></li>
                                            </ul>
                                        </div>

                                        {/* MOOV Column */}
                                        <div className={styles.linkColumn}>
                                            <h4><Link href="/crm-para-imobiliarias">MOOV</Link></h4>
                                            <ul>
                                                <li><Link href="/crm-para-imobiliarias/site-imobiliario">Site</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/gestao-de-leads">CRM</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/whatsapp">WhatsApp</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/gestao-de-tarefas">Tarefas</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/automacoes">Automações</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/aplicativo">Aplicativo</Link></li>
                                                <li><Link href="/crm-para-imobiliarias/atendimento-imobiliario">Sitouch</Link></li>
                                            </ul>
                                        </div>

                                        {/* Apps Column */}
                                        <div className={styles.linkColumn}>
                                            <h4><Link href="/aplicativos-para-imobiliarias">Aplicativos</Link></h4>
                                            <ul>
                                                <li><Link href="/aplicativos-para-imobiliarias/relacionamento-com-inquilinos">Sitalk</Link></li>
                                                <li><Link href="/aplicativos-para-imobiliarias/gestao-operacional-imobiliaria">Tools</Link></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        </div>

                        {/* Funcionalidades */}
                        <div
                            className={`${styles.navItem} ${expandedItem === "funcionalidades" ? styles.navItemActive : ''}`}
                            onClick={() => toggleAccordion("funcionalidades")}
                        >
                            <div className={styles.navItemContent}>
                                <span className={styles.navText}>Funcionalidades</span>
                                <Image src={IMG_CHEVRON} alt="Expand" width={16} height={16} className={styles.chevron} />
                            </div>

                            <div className={styles.megaMenuWrapper}>
                                <div className={styles.megaMenuContainer}>
                                    <div className={styles.promoBox}>
                                        <h3>Funcionalidades para cada área da imobiliária</h3>
                                        <p>Acesse os módulos principais do ecossistema ou veja todas as funcionalidades específicas em uma página completa.</p>
                                    </div>

                                    <div className={styles.modulesContent}>
                                        <div>
                                            <div className={styles.moduleSectionTitle}>Módulos do SIMOB</div>
                                            <div className={styles.moduleGrid}>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/locacao">
                                                    <span className={styles.moduleIcon}>LO</span>
                                                    <div><h3>Locação</h3><p>Contratos, imóveis, repasses e rotina operacional</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/financeiro">
                                                    <span className={styles.moduleIcon}>FI</span>
                                                    <div><h3>Financeiro</h3><p>Boletos, PIX, contas, conciliação e NFSe</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/vendas">
                                                    <span className={styles.moduleIcon}>VE</span>
                                                    <div><h3>Vendas</h3><p>Imóveis, contratos, parcelas e comissões</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/condominio">
                                                    <span className={styles.moduleIcon}>CO</span>
                                                    <div><h3>Condomínio</h3><p>Unidades, rateios, leituras e cobranças</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/juridico">
                                                    <span className={styles.moduleIcon}>JU</span>
                                                    <div><h3>Jurídico</h3><p>Acordos, renegociações e honorários</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/caixa">
                                                    <span className={styles.moduleIcon}>CX</span>
                                                    <div><h3>Caixa</h3><p>Entradas, saídas e recebimentos</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/sistema-para-imobiliaria/bi">
                                                    <span className={styles.moduleIcon}>BI</span>
                                                    <div><h3>Business Intelligence</h3><p>Dashboards, relatórios e indicadores</p></div>
                                                </Link>
                                            </div>
                                        </div>

                                        <div>
                                            <div className={styles.moduleSectionTitle}>MOOV e Aplicativos</div>
                                            <div className={styles.moduleGridSingle}>
                                                <Link className={styles.moduleLink} href="/crm-para-imobiliarias">
                                                    <span className={styles.moduleIcon}>MO</span>
                                                    <div><h3>MOOV</h3><p>Site imobiliário, CRM e gestão de leads</p></div>
                                                </Link>
                                                <Link className={styles.moduleLink} href="/aplicativos-para-imobiliarias">
                                                    <span className={styles.moduleIcon}>AP</span>
                                                    <div><h3>Aplicativos</h3><p>SiTalk, Tools, vistorias e operação em campo</p></div>
                                                </Link>
                                            </div>

                                            <div className={styles.hubBox}>
                                                <h3>Funcionalidades específicas</h3>
                                                <p>Plano de contas, Open Finance, DIMOB, PJBank, vistorias, manutenção, Scaza, WhatsApp e mais.</p>
                                                <Link href="/funcionalidades">Ver todas as funcionalidades</Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recursos */}
                        <div
                            className={`${styles.navItem} ${expandedItem === "recursos" ? styles.navItemActive : ''}`}
                            onClick={() => toggleAccordion("recursos")}
                        >
                            <div className={styles.navItemContent}>
                                <span className={styles.navText}>Recursos</span>
                                <Image src={IMG_CHEVRON} alt="Expand" width={16} height={16} className={styles.chevron} />
                            </div>

                            <div className={styles.megaMenuWrapper}>
                                <div className={styles.megaMenuContainer}>
                                    {/* Left Promo Box */}
                                    <div className={styles.promoBox}>
                                        <h3>Recursos para evoluir a operação imobiliária</h3>
                                        <p>Conteúdos, guias e materiais que ajudam você a entender melhor sua rotina, tomar decisões mais seguras e extrair mais valor da tecnologia.</p>
                                    </div>

                                    {/* Right Links Columns */}
                                    <div className={styles.linksColumns}>
                                        {/* Education Column */}
                                        <div className={styles.linkColumn}>
                                            <h4>Educação</h4>
                                            <ul>
                                                <li><Link target="_blank" href="https://flexpro.evolutto.com.br/">Flexpro Academy</Link></li>
                                                <li><Link href="/central-de-recursos">Central de recursos</Link></li>
                                                <li><Link href="/blog">Blog</Link></li>
                                            </ul>
                                        </div>

                                        {/* Support Column */}
                                        <div className={styles.linkColumn}>
                                            <h4>Suporte</h4>
                                            <ul>
                                                <li><Link target="_blank" href="https://documenter.getpostman.com/view/1724124/TVRecVa8">Documentação</Link></li>
                                                <li><Link href="/faq">Dúvidas frequentes</Link></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Empresa */}
                        <div
                            className={`${styles.navItem} ${expandedItem === "empresa" ? styles.navItemActive : ''}`}
                            onClick={() => toggleAccordion("empresa")}
                        >
                            <div className={styles.navItemContent}>
                                <span className={styles.navText}>Empresa</span>
                                <Image src={IMG_CHEVRON} alt="Expand" width={16} height={16} className={styles.chevron} />
                            </div>

                            <div className={styles.megaMenuWrapper}>
                                <div className={styles.megaMenuContainer}>
                                    {/* Left Promo Box */}
                                    <div className={styles.promoBox}>
                                        <h3>Mais do que tecnologia, uma empresa construída para a rotina imobiliária</h3>
                                        <p>Conheça a Flexpro, nossa visão e as pessoas por trás da plataforma que ajuda imobiliárias a crescer com controle.</p>
                                    </div>

                                    {/* Right Links Columns */}
                                    <div className={styles.linksColumns}>
                                        <div className={styles.linkColumn}>
                                            <ul>
                                                <li><Link href="/sobre-a-flexpro">Sobre nós</Link></li>
                                                <li>
                                                    <Link href="https://wa.me/551142805787" target="_blank" rel="noopener noreferrer">
                                                        Agendar uma demo
                                                    </Link>
                                                </li>
                                                <li><Link href="/carreiras">Carreiras</Link></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </nav>

                    <div className={styles.mobileCta}>
                        <Button href="https://wa.me/551142805787" target="_blank" rel="noopener noreferrer">
                            Agendar uma demo
                        </Button>
                    </div>
                </div>
            </div>
        </header>
    );
}
